/**
 * Super Admin Redux Reducers
 * 
 * These reducers manage the Super Admin state slice in the Redux store.
 * Each reducer handles a specific aspect of Super Admin functionality.
 * 
 * State Shape:
 * - platformAnalytics: Platform-wide metrics and statistics
 * - tenantList: Paginated list of all tenants
 * - tenantDetails: Single tenant detailed information
 * - tenantAnalytics: Single tenant analytics
 * - tenantCreate: Create tenant operation state
 * - tenantStatusUpdate: Status update operation state
 * - auditLogs: Paginated audit log entries
 * - auditStats: Audit statistics
 * 
 * @module SuperAdmin
 * @category Reducers
 */

import {
    PLATFORM_ANALYTICS_REQUEST,
    PLATFORM_ANALYTICS_SUCCESS,
    PLATFORM_ANALYTICS_FAIL,
    TENANT_LIST_REQUEST,
    TENANT_LIST_SUCCESS,
    TENANT_LIST_FAIL,
    TENANT_DETAILS_REQUEST,
    TENANT_DETAILS_SUCCESS,
    TENANT_DETAILS_FAIL,
    TENANT_USERS_REQUEST,
    TENANT_USERS_SUCCESS,
    TENANT_USERS_FAIL,
    TENANT_DETAILS_RESET,
    TENANT_ANALYTICS_REQUEST,
    TENANT_ANALYTICS_SUCCESS,
    TENANT_ANALYTICS_FAIL,
    TENANT_CREATE_REQUEST,
    TENANT_CREATE_SUCCESS,
    TENANT_CREATE_FAIL,
    TENANT_CREATE_RESET,
    TENANT_STATUS_UPDATE_REQUEST,
    TENANT_STATUS_UPDATE_SUCCESS,
    TENANT_STATUS_UPDATE_FAIL,
    TENANT_STATUS_UPDATE_RESET,
    AUDIT_LOGS_REQUEST,
    AUDIT_LOGS_SUCCESS,
    AUDIT_LOGS_FAIL,
    AUDIT_STATS_REQUEST,
    AUDIT_STATS_SUCCESS,
    AUDIT_STATS_FAIL,
    PLATFORM_USER_LIST_REQUEST,
    PLATFORM_USER_LIST_SUCCESS,
    PLATFORM_USER_LIST_FAIL,
    PLATFORM_USER_CREATE_REQUEST,
    PLATFORM_USER_CREATE_SUCCESS,
    PLATFORM_USER_CREATE_FAIL,
    PLATFORM_USER_CREATE_RESET,
    PLATFORM_USER_UPDATE_REQUEST,
    PLATFORM_USER_UPDATE_SUCCESS,
    PLATFORM_USER_UPDATE_FAIL,
    PLATFORM_USER_UPDATE_RESET,
    PLATFORM_USER_STATUS_REQUEST,
    PLATFORM_USER_STATUS_SUCCESS,
    PLATFORM_USER_STATUS_FAIL,
    PLATFORM_USER_STATUS_RESET,
    TENANT_COMMERCIAL_TERMS_REQUEST,
    TENANT_COMMERCIAL_TERMS_SUCCESS,
    TENANT_COMMERCIAL_TERMS_FAIL,
    TENANT_COMMERCIAL_TERMS_UPDATE_REQUEST,
    TENANT_COMMERCIAL_TERMS_UPDATE_SUCCESS,
    TENANT_COMMERCIAL_TERMS_UPDATE_FAIL,
    TENANT_COMMERCIAL_TERMS_UPDATE_RESET,
    OWNER_REPORT_CONFIG_LIST_REQUEST,
    OWNER_REPORT_CONFIG_LIST_SUCCESS,
    OWNER_REPORT_CONFIG_LIST_FAIL,
    OWNER_REPORT_CONFIG_DETAILS_REQUEST,
    OWNER_REPORT_CONFIG_DETAILS_SUCCESS,
    OWNER_REPORT_CONFIG_DETAILS_FAIL,
    OWNER_REPORT_CONFIG_DETAILS_RESET,
    OWNER_REPORT_CONFIG_CREATE_REQUEST,
    OWNER_REPORT_CONFIG_CREATE_SUCCESS,
    OWNER_REPORT_CONFIG_CREATE_FAIL,
    OWNER_REPORT_CONFIG_CREATE_RESET,
    OWNER_REPORT_CONFIG_UPDATE_REQUEST,
    OWNER_REPORT_CONFIG_UPDATE_SUCCESS,
    OWNER_REPORT_CONFIG_UPDATE_FAIL,
    OWNER_REPORT_CONFIG_UPDATE_RESET,
    OWNER_REPORT_CONFIG_STATUS_REQUEST,
    OWNER_REPORT_CONFIG_STATUS_SUCCESS,
    OWNER_REPORT_CONFIG_STATUS_FAIL,
    OWNER_REPORT_CONFIG_STATUS_RESET,
    OWNER_REPORT_RUN_NOW_REQUEST,
    OWNER_REPORT_RUN_NOW_SUCCESS,
    OWNER_REPORT_RUN_NOW_FAIL,
    OWNER_REPORT_RUN_NOW_RESET,
    OWNER_REPORT_RUNS_REQUEST,
    OWNER_REPORT_RUNS_SUCCESS,
    OWNER_REPORT_RUNS_FAIL,
    OWNER_REPORT_RUN_DETAILS_REQUEST,
    OWNER_REPORT_RUN_DETAILS_SUCCESS,
    OWNER_REPORT_RUN_DETAILS_FAIL,
    OWNER_REPORT_RUN_DETAILS_RESET,
} from '../constants/superAdminConstants';

// ============================================================================
// PLATFORM ANALYTICS REDUCER
// ============================================================================

/**
 * Manages platform-wide analytics state.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   analytics: {
 *     tenants: { total, active, suspended },
 *     users: { total, byRole },
 *     revenue: { total, demandNotices, etickets },
 *     demandNotices: { total, paid, pending },
 *     topPerformingTenants: Array,
 *     recentActivity: { demandNoticesLast24h }
 *   },
 *   error: string | null
 * }
 */
export const platformAnalyticsReducer = (state = { analytics: null }, action) => {
    switch (action.type) {
        case PLATFORM_ANALYTICS_REQUEST:
            return { loading: true, analytics: state.analytics };
        case PLATFORM_ANALYTICS_SUCCESS:
            return { loading: false, analytics: action.payload };
        case PLATFORM_ANALYTICS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

// ============================================================================
// TENANT LIST REDUCER
// ============================================================================

/**
 * Manages the paginated list of tenants.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   tenants: Array,
 *   pagination: {
 *     currentPage: number,
 *     totalPages: number,
 *     totalCount: number,
 *     hasNextPage: boolean,
 *     hasPreviousPage: boolean
 *   },
 *   error: string | null
 * }
 */
export const tenantListReducer = (state = { tenants: [] }, action) => {
    switch (action.type) {
        case TENANT_LIST_REQUEST:
            return { loading: true, tenants: state.tenants };
        case TENANT_LIST_SUCCESS:
            return {
                loading: false,
                tenants: action.payload.tenants,
                pagination: action.payload.pagination
            };
        case TENANT_LIST_FAIL:
            return { loading: false, error: action.payload, tenants: [] };
        default:
            return state;
    }
};

// ============================================================================
// TENANT DETAILS REDUCER
// ============================================================================

/**
 * Manages single tenant detailed information.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   tenant: {
 *     _id: string,
 *     name: string,
 *     about: string,
 *     logoUrl: string,
 *     ownerUserId: string,
 *     settings: Object,
 *     ownerUser: Object,
 *     users: Array,
 *     userCount: number
 *   },
 *   error: string | null
 * }
 */
export const tenantDetailsReducer = (state = { tenant: null }, action) => {
    switch (action.type) {
        case TENANT_DETAILS_REQUEST:
            return { loading: true, tenant: state.tenant };
        case TENANT_DETAILS_SUCCESS:
            return { loading: false, tenant: action.payload };
        case TENANT_DETAILS_FAIL:
            return { loading: false, error: action.payload };
        case TENANT_DETAILS_RESET:
            return { tenant: null };
        default:
            return state;
    }
};

export const tenantUsersReducer = (state = { users: [] }, action) => {
    switch (action.type) {
        case TENANT_USERS_REQUEST:
            return { loading: true, users: state.users, pagination: state.pagination };
        case TENANT_USERS_SUCCESS:
            return {
                loading: false,
                users: action.payload.users,
                pagination: action.payload.pagination
            };
        case TENANT_USERS_FAIL:
            return { loading: false, error: action.payload, users: [] };
        default:
            return state;
    }
};

// ============================================================================
// TENANT ANALYTICS REDUCER
// ============================================================================

/**
 * Manages single tenant analytics data.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   analytics: {
 *     tenant: Object,
 *     settings: Object,
 *     ownerUser: Object,
 *     stats: {
 *       userCount: number,
 *       demandNoticeCount: number,
 *       paidDemandNoticeCount: number,
 *       unpaidDemandNoticeCount: number,
 *       revenueLineCount: number,
 *       payerCount: number,
 *       eticketCount: number,
 *       totalRevenue: number,
 *       demandNoticeRevenue: number,
 *       eticketRevenue: number,
 *       collectionRate: string
 *     }
 *   },
 *   error: string | null
 * }
 */
export const tenantAnalyticsReducer = (state = { analytics: null }, action) => {
    switch (action.type) {
        case TENANT_ANALYTICS_REQUEST:
            return { loading: true, analytics: state.analytics };
        case TENANT_ANALYTICS_SUCCESS:
            return { loading: false, analytics: action.payload };
        case TENANT_ANALYTICS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

// ============================================================================
// TENANT CREATE REDUCER
// ============================================================================

/**
 * Manages tenant creation operation state.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   success: boolean,
 *   tenant: Object | null,
 *   error: string | null
 * }
 */
export const tenantCreateReducer = (state = {}, action) => {
    switch (action.type) {
        case TENANT_CREATE_REQUEST:
            return { loading: true };
        case TENANT_CREATE_SUCCESS:
            return { loading: false, success: true, tenant: action.payload };
        case TENANT_CREATE_FAIL:
            return { loading: false, error: action.payload };
        case TENANT_CREATE_RESET:
            return {};
        default:
            return state;
    }
};

// ============================================================================
// TENANT STATUS UPDATE REDUCER
// ============================================================================

/**
 * Manages tenant status update operation state.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   success: boolean,
 *   settings: Object | null,
 *   error: string | null
 * }
 */
export const tenantStatusUpdateReducer = (state = {}, action) => {
    switch (action.type) {
        case TENANT_STATUS_UPDATE_REQUEST:
            return { loading: true };
        case TENANT_STATUS_UPDATE_SUCCESS:
            return { loading: false, success: true, settings: action.payload };
        case TENANT_STATUS_UPDATE_FAIL:
            return { loading: false, error: action.payload };
        case TENANT_STATUS_UPDATE_RESET:
            return {};
        default:
            return state;
    }
};

// ============================================================================
// AUDIT LOGS REDUCER
// ============================================================================

/**
 * Manages paginated audit log entries.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   logs: Array,
 *   pagination: Object,
 *   error: string | null
 * }
 */
export const auditLogsReducer = (state = { logs: [] }, action) => {
    switch (action.type) {
        case AUDIT_LOGS_REQUEST:
            return { loading: true, logs: state.logs };
        case AUDIT_LOGS_SUCCESS:
            return {
                loading: false,
                logs: action.payload.logs,
                pagination: action.payload.pagination
            };
        case AUDIT_LOGS_FAIL:
            return { loading: false, error: action.payload, logs: [] };
        default:
            return state;
    }
};

// ============================================================================
// AUDIT STATS REDUCER
// ============================================================================

/**
 * Manages audit statistics.
 * 
 * State shape:
 * {
 *   loading: boolean,
 *   stats: {
 *     totalActions: number,
 *     successfulActions: number,
 *     failedActions: number,
 *     uniqueUsersCount: number,
 *     categoryBreakdown: Object
 *   },
 *   error: string | null
 * }
 */
export const auditStatsReducer = (state = { stats: null }, action) => {
    switch (action.type) {
        case AUDIT_STATS_REQUEST:
            return { loading: true, stats: state.stats };
        case AUDIT_STATS_SUCCESS:
            return { loading: false, stats: action.payload };
        case AUDIT_STATS_FAIL:
            return { loading: false, error: action.payload };
        default:
            return state;
    }
};

export const platformUserListReducer = (state = { users: [] }, action) => {
    switch (action.type) {
        case PLATFORM_USER_LIST_REQUEST:
            return { loading: true, users: state.users, pagination: state.pagination };
        case PLATFORM_USER_LIST_SUCCESS:
            return { loading: false, users: action.payload.users, pagination: action.payload.pagination };
        case PLATFORM_USER_LIST_FAIL:
            return { loading: false, error: action.payload, users: [] };
        default:
            return state;
    }
};

export const platformUserCreateReducer = (state = {}, action) => {
    switch (action.type) {
        case PLATFORM_USER_CREATE_REQUEST:
            return { loading: true };
        case PLATFORM_USER_CREATE_SUCCESS:
            return { loading: false, success: true, user: action.payload };
        case PLATFORM_USER_CREATE_FAIL:
            return { loading: false, error: action.payload };
        case PLATFORM_USER_CREATE_RESET:
            return {};
        default:
            return state;
    }
};

export const platformUserUpdateReducer = (state = {}, action) => {
    switch (action.type) {
        case PLATFORM_USER_UPDATE_REQUEST:
            return { loading: true };
        case PLATFORM_USER_UPDATE_SUCCESS:
            return { loading: false, success: true, user: action.payload };
        case PLATFORM_USER_UPDATE_FAIL:
            return { loading: false, error: action.payload };
        case PLATFORM_USER_UPDATE_RESET:
            return {};
        default:
            return state;
    }
};

export const platformUserStatusReducer = (state = {}, action) => {
    switch (action.type) {
        case PLATFORM_USER_STATUS_REQUEST:
            return { loading: true };
        case PLATFORM_USER_STATUS_SUCCESS:
            return { loading: false, success: true, user: action.payload };
        case PLATFORM_USER_STATUS_FAIL:
            return { loading: false, error: action.payload };
        case PLATFORM_USER_STATUS_RESET:
            return {};
        default:
            return state;
    }
};

export const tenantCommercialTermsReducer = (state = { terms: null }, action) => {
    switch (action.type) {
        case TENANT_COMMERCIAL_TERMS_REQUEST:
            return { loading: true, terms: state.terms };
        case TENANT_COMMERCIAL_TERMS_SUCCESS:
            return { loading: false, terms: action.payload };
        case TENANT_COMMERCIAL_TERMS_FAIL:
            return { loading: false, error: action.payload, terms: null };
        default:
            return state;
    }
};

export const tenantCommercialTermsUpdateReducer = (state = {}, action) => {
    switch (action.type) {
        case TENANT_COMMERCIAL_TERMS_UPDATE_REQUEST:
            return { loading: true };
        case TENANT_COMMERCIAL_TERMS_UPDATE_SUCCESS:
            return { loading: false, success: true, terms: action.payload };
        case TENANT_COMMERCIAL_TERMS_UPDATE_FAIL:
            return { loading: false, error: action.payload };
        case TENANT_COMMERCIAL_TERMS_UPDATE_RESET:
            return {};
        default:
            return state;
    }
};

export const ownerReportConfigListReducer = (state = { configs: [] }, action) => {
    switch (action.type) {
        case OWNER_REPORT_CONFIG_LIST_REQUEST:
            return { loading: true, configs: state.configs };
        case OWNER_REPORT_CONFIG_LIST_SUCCESS:
            return { loading: false, configs: action.payload.configs, pagination: action.payload.pagination };
        case OWNER_REPORT_CONFIG_LIST_FAIL:
            return { loading: false, error: action.payload, configs: [] };
        default:
            return state;
    }
};

export const ownerReportConfigDetailsReducer = (state = { config: null }, action) => {
    switch (action.type) {
        case OWNER_REPORT_CONFIG_DETAILS_REQUEST:
            return { loading: true, config: state.config };
        case OWNER_REPORT_CONFIG_DETAILS_SUCCESS:
            return { loading: false, config: action.payload };
        case OWNER_REPORT_CONFIG_DETAILS_FAIL:
            return { loading: false, error: action.payload, config: null };
        case OWNER_REPORT_CONFIG_DETAILS_RESET:
            return { config: null };
        default:
            return state;
    }
};

export const ownerReportConfigCreateReducer = (state = {}, action) => {
    switch (action.type) {
        case OWNER_REPORT_CONFIG_CREATE_REQUEST:
            return { loading: true };
        case OWNER_REPORT_CONFIG_CREATE_SUCCESS:
            return { loading: false, success: true, config: action.payload };
        case OWNER_REPORT_CONFIG_CREATE_FAIL:
            return { loading: false, error: action.payload };
        case OWNER_REPORT_CONFIG_CREATE_RESET:
            return {};
        default:
            return state;
    }
};

export const ownerReportConfigUpdateReducer = (state = {}, action) => {
    switch (action.type) {
        case OWNER_REPORT_CONFIG_UPDATE_REQUEST:
            return { loading: true };
        case OWNER_REPORT_CONFIG_UPDATE_SUCCESS:
            return { loading: false, success: true, config: action.payload };
        case OWNER_REPORT_CONFIG_UPDATE_FAIL:
            return { loading: false, error: action.payload };
        case OWNER_REPORT_CONFIG_UPDATE_RESET:
            return {};
        default:
            return state;
    }
};

export const ownerReportConfigStatusReducer = (state = {}, action) => {
    switch (action.type) {
        case OWNER_REPORT_CONFIG_STATUS_REQUEST:
            return { loading: true };
        case OWNER_REPORT_CONFIG_STATUS_SUCCESS:
            return { loading: false, success: true, config: action.payload };
        case OWNER_REPORT_CONFIG_STATUS_FAIL:
            return { loading: false, error: action.payload };
        case OWNER_REPORT_CONFIG_STATUS_RESET:
            return {};
        default:
            return state;
    }
};

export const ownerReportRunNowReducer = (state = {}, action) => {
    switch (action.type) {
        case OWNER_REPORT_RUN_NOW_REQUEST:
            return { loading: true };
        case OWNER_REPORT_RUN_NOW_SUCCESS:
            return { loading: false, success: true, runResult: action.payload };
        case OWNER_REPORT_RUN_NOW_FAIL:
            return { loading: false, error: action.payload };
        case OWNER_REPORT_RUN_NOW_RESET:
            return {};
        default:
            return state;
    }
};

export const ownerReportRunsReducer = (state = { runs: [] }, action) => {
    switch (action.type) {
        case OWNER_REPORT_RUNS_REQUEST:
            return { loading: true, runs: state.runs };
        case OWNER_REPORT_RUNS_SUCCESS:
            return { loading: false, runs: action.payload.runs, pagination: action.payload.pagination };
        case OWNER_REPORT_RUNS_FAIL:
            return { loading: false, error: action.payload, runs: [] };
        default:
            return state;
    }
};

export const ownerReportRunDetailsReducer = (state = { run: null }, action) => {
    switch (action.type) {
        case OWNER_REPORT_RUN_DETAILS_REQUEST:
            return { loading: true, run: state.run };
        case OWNER_REPORT_RUN_DETAILS_SUCCESS:
            return { loading: false, run: action.payload };
        case OWNER_REPORT_RUN_DETAILS_FAIL:
            return { loading: false, error: action.payload, run: null };
        case OWNER_REPORT_RUN_DETAILS_RESET:
            return { run: null };
        default:
            return state;
    }
};
